/**
 * /onboarding/setup — self-serve provisioning wizard.
 *
 * Shown to invited customers immediately after accepting their invitation
 * (accept-invite.tsx redirects here when the user has no entities).
 * Also accessible directly if a logged-in user somehow lands with no entity.
 *
 * Four steps:
 *   1. Company details  → POST /api/onboarding/provision
 *   2. Pick a plan      → POST /api/onboarding/subscribe → Stripe redirect
 *   3. (Stripe handles payment, redirects to /onboarding/complete)
 *   4. Import your books → link to QB import or skip
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errorMessage, setToken } from "@/lib/api";
import type { Plan } from "@/lib/types";
import { saasPlansQueryOptions } from "@/lib/queries";

export const Route = createFileRoute("/_auth/onboarding/setup")({
  validateSearch: (search: Record<string, unknown>): OnboardingSearch => ({
    mode: search.mode === "demo" ? "demo" : undefined,
    profile: typeof search.profile === "string" && search.profile.trim()
      ? search.profile.trim()
      : undefined,
  }),
  component: OnboardingSetupPage,
});

// ── Types ─────────────────────────────────────────────────────────────────────

interface ProvisionResponse {
  entity_id: string;
  entity_code: string;
  bootstrapped: boolean;
  demo_mode: boolean;
  demo_profile: string | null;
  demo_seed_summary: Record<string, unknown> | null;
}

interface DemoProfile {
  key: string;
  label: string;
  description: string;
  example_company: string;
  pack_flags: Record<string, boolean>;
  highlights: string[];
}

interface SubscribeResponse {
  subscription_id: string;
  checkout_url: string;
}

interface OnboardingSearch {
  mode?: "demo";
  profile?: string;
}

interface EntityHandoff {
  result: ProvisionResponse;
  purpose: "demo" | "live";
  error?: string;
}

function demoTourPrompt(profile: string | null): string {
  if (profile === "concrete_pumping") {
    return "Give me the concrete pumping tour for this sample company.";
  }
  if (["manufacturing", "rental", "services"].includes(profile ?? "")) {
    return `Give me the ${profile} industry-pack tour for this sample company.`;
  }
  if (profile === "saas") {
    return "Give me the contract-to-cash and ASC 606 tour for this SaaS sample company.";
  }
  return `Give me the full tour of this ${profile ?? "general business"} sample company.`;
}

// ── Step indicator ────────────────────────────────────────────────────────────

const STEPS = ["Your company", "Choose a plan", "Payment", "Import books"] as const;

function StepBar({ current }: { current: number }) {
  return (
    <ol className="flex items-center gap-0 mb-10">
      {STEPS.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li key={label} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div
                className={[
                  "w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold",
                  done
                    ? "bg-blue-600 text-white"
                    : active
                    ? "bg-blue-600 text-white ring-4 ring-blue-100"
                    : "bg-slate-200 text-slate-500 dark:bg-gray-700 dark:text-slate-400",
                ].join(" ")}
              >
                {done ? "✓" : i + 1}
              </div>
              <span
                className={[
                  "mt-1 text-xs text-center whitespace-nowrap",
                  active
                    ? "text-blue-700 font-medium dark:text-blue-400"
                    : done
                    ? "text-blue-600 dark:text-blue-400"
                    : "text-slate-400 dark:text-slate-500",
                ].join(" ")}
              >
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div
                className={[
                  "flex-1 h-0.5 mx-2 mt-[-14px]",
                  i < current ? "bg-blue-600" : "bg-slate-200 dark:bg-gray-700",
                ].join(" ")}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

function OnboardingSetupPage() {
  const search = Route.useSearch();
  const demoJourney = search.mode === "demo";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [provisionResult, setProvisionResult] = useState<ProvisionResponse | null>(null);
  const [handoff, setHandoff] = useState<EntityHandoff | null>(null);

  const activateEntity = async (
    result: ProvisionResponse,
    purpose: EntityHandoff["purpose"],
  ) => {
    setHandoff({ result, purpose });
    try {
      const switched = await api<{ access_token: string }>(
        "/api/auth/switch-entity",
        { method: "POST", body: { entity_id: result.entity_id } },
      );
      setToken(switched.access_token);
      qc.clear();
      await navigate({ to: "/" });
      if (purpose === "demo") {
        window.setTimeout(() => {
          window.dispatchEvent(new CustomEvent("bynaus:open-agent", {
            detail: {
              prompt: demoTourPrompt(result.demo_profile),
              autoSend: true,
            },
          }));
        }, 250);
      }
    } catch (err) {
      setHandoff({ result, purpose, error: errorMessage(err) });
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-gray-950 flex flex-col items-center py-16 px-4">
      <div className="w-full max-w-2xl">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
            {demoJourney ? "Choose a sample company" : "Welcome to Bynaus"}
          </h1>
          <p className="mt-2 text-slate-500 dark:text-slate-400">
            {demoJourney
              ? "Pick an industry and Ledger Core will build the demo and start the matching CFO tour."
              : "Let's get your company set up in about 2 minutes."}
          </p>
        </div>

        {!demoJourney && <StepBar current={step} />}

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 dark:bg-gray-900 dark:border-gray-800">
          {handoff && (
            <div className="space-y-5 py-4 text-center">
              <div className="text-4xl" aria-hidden="true">
                {handoff.error ? "⚠️" : handoff.purpose === "demo" ? "✨" : "✓"}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                  {handoff.error
                    ? "Your company was created, but we could not open it"
                    : handoff.purpose === "demo"
                    ? "Opening your sample company…"
                    : "Opening your company…"}
                </h2>
                <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                  {handoff.error
                    ? handoff.error
                    : handoff.purpose === "demo"
                    ? "We are switching into the separate Demo workspace and preparing the matching CFO tour."
                    : "We are switching Ledger Core into your new company."}
                </p>
              </div>
              {handoff.error && (
                <div className="flex flex-col justify-center gap-2 sm:flex-row">
                  <button
                    type="button"
                    onClick={() => void activateEntity(handoff.result, handoff.purpose)}
                    className="rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700"
                  >
                    Try opening it again
                  </button>
                  <button
                    type="button"
                    onClick={() => setHandoff(null)}
                    className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-gray-700 dark:text-slate-200 dark:hover:bg-gray-800"
                  >
                    Return to setup
                  </button>
                </div>
              )}
            </div>
          )}
          {!handoff && step === 0 && (
            <CompanyStep
              initialDemoMode={search.mode === "demo"}
              initialDemoProfile={search.profile}
              onDone={(result) => {
                setProvisionResult(result);
                qc.invalidateQueries({ queryKey: ["auth", "me", "entities"] });
                if (result.demo_mode) {
                  void activateEntity(result, "demo");
                } else {
                  setStep(1);
                }
              }}
            />
          )}
          {!handoff && step === 1 && provisionResult && (
            <PlanStep
              entityId={provisionResult.entity_id}
              onSkip={() => setStep(3)}
            />
          )}
          {!handoff && step === 2 && (
            // Stripe handles this step externally; user lands at /onboarding/complete
            // which redirects here. Shown briefly as a transition state.
            <div className="text-center py-8">
              <div className="text-4xl mb-4">💳</div>
              <p className="text-slate-600 dark:text-slate-300">
                Redirecting to payment…
              </p>
            </div>
          )}
          {!handoff && step === 3 && provisionResult && (
            <ImportStep
              onDone={() => {
                void activateEntity(provisionResult, "live");
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Step 1: Company details ───────────────────────────────────────────────────

function CompanyStep({
  initialDemoMode,
  initialDemoProfile,
  onDone,
}: {
  initialDemoMode: boolean;
  initialDemoProfile?: string;
  onDone: (r: ProvisionResponse) => void;
}) {
  const [legalName, setLegalName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [ein, setEin] = useState("");
  const [fyMonth, setFyMonth] = useState(1);
  const [whatBuilding, setWhatBuilding] = useState("");
  const [whoCustomers, setWhoCustomers] = useState("");
  const [accountingModel, setAccountingModel] = useState("owner_only");
  const [accountingPeople, setAccountingPeople] = useState(1);
  const [independentReviewer, setIndependentReviewer] = useState(false);
  const [prioritySources, setPrioritySources] = useState<string[]>([]);
  const [materialityThreshold, setMaterialityThreshold] = useState("");
  const [demoMode, setDemoMode] = useState(initialDemoMode);
  const [demoProfile, setDemoProfile] = useState(initialDemoProfile ?? "construction");
  const [error, setError] = useState<string | null>(null);

  const demoProfilesQuery = useQuery({
    queryKey: ["onboarding", "demo-profiles"],
    queryFn: () => api<DemoProfile[]>("/api/onboarding/demo-profiles"),
    staleTime: Infinity,
  });
  const demoProfiles = demoProfilesQuery.data ?? [];
  const selectedDemoProfile = demoProfiles.find((profile) => profile.key === demoProfile);

  useEffect(() => {
    if (!demoMode || demoProfiles.length === 0 || selectedDemoProfile) return;
    setDemoProfile(demoProfiles[0].key);
  }, [demoMode, demoProfiles, selectedDemoProfile]);

  useEffect(() => {
    if (!demoMode || legalName.trim() || !selectedDemoProfile) return;
    setLegalName(selectedDemoProfile.example_company);
    setDisplayName(selectedDemoProfile.example_company);
  }, [demoMode, legalName, selectedDemoProfile]);

  const selectDemoProfile = (key: string) => {
    const next = demoProfiles.find((profile) => profile.key === key);
    const priorExample = selectedDemoProfile?.example_company;
    setDemoProfile(key);
    if (next && (!legalName.trim() || legalName === priorExample)) {
      setLegalName(next.example_company);
      setDisplayName(next.example_company);
    }
  };

  const provision = useMutation({
    mutationFn: () =>
      api<ProvisionResponse>("/api/onboarding/provision", {
        method: "POST",
        body: {
          legal_name: legalName.trim(),
          display_name: displayName.trim() || legalName.trim(),
          ein: ein.trim() || undefined,
          fiscal_year_start_month: fyMonth,
          what_are_you_building: whatBuilding.trim() || undefined,
          who_are_your_customers: whoCustomers.trim() || undefined,
          accounting_operating_model: accountingModel,
          accounting_people_count: ["owner_only", "not_sure"].includes(accountingModel) ? 1 : accountingPeople,
          independent_reviewer_expected: ["owner_only", "not_sure"].includes(accountingModel) ? false : independentReviewer,
          priority_control_sources: prioritySources,
          control_materiality_threshold: materialityThreshold || undefined,
          demo_mode: demoMode,
          demo_profile: demoMode ? demoProfile : undefined,
        },
      }),
    onSuccess: onDone,
    onError: (err) => setError(errorMessage(err)),
  });

  const submitProvision = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    provision.mutate();
  };

  if (demoMode) {
    return (
      <form onSubmit={submitProvision} className="space-y-6">
        <div>
          <div className="flex items-center gap-2">
            <span className="rounded-full bg-blue-100 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-blue-700 dark:bg-blue-950 dark:text-blue-300">
              Demo
            </span>
            <span className="text-xs font-medium text-emerald-700 dark:text-emerald-300">
              Dummy data · no live connections
            </span>
          </div>
          <h2 className="mt-3 text-xl font-semibold text-slate-900 dark:text-slate-100">
            What kind of business should we show you?
          </h2>
          <p className="mt-1 text-sm leading-6 text-slate-500 dark:text-slate-400">
            Choose the closest match. You can create another sample later without changing your live company.
          </p>
        </div>

        {demoProfilesQuery.isLoading && (
          <div className="grid gap-3 sm:grid-cols-2" aria-label="Loading industry samples">
            {[0, 1, 2, 3].map((item) => (
              <div key={item} className="h-28 animate-pulse rounded-xl bg-slate-100 dark:bg-gray-800" />
            ))}
          </div>
        )}

        {demoProfilesQuery.isError && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
            <p className="font-semibold">Industry samples could not be loaded.</p>
            <p className="mt-1">Refresh this page to try again, or return to the launch guide.</p>
            <div className="mt-3 flex gap-3">
              <button type="button" onClick={() => void demoProfilesQuery.refetch()} className="font-semibold underline">
                Try again
              </button>
              <a href="/ledger-core-launch.html" className="font-semibold underline">Launch guide</a>
            </div>
          </div>
        )}

        {demoProfiles.length > 0 && (
          <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Industry sample">
            {demoProfiles.map((profile) => {
              const selected = profile.key === demoProfile;
              return (
                <button
                  key={profile.key}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => selectDemoProfile(profile.key)}
                  className={[
                    "rounded-xl border-2 p-4 text-left transition-all",
                    selected
                      ? "border-blue-600 bg-blue-50 shadow-sm dark:border-blue-500 dark:bg-blue-950/30"
                      : "border-slate-200 bg-white hover:border-blue-300 hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:border-blue-700",
                  ].join(" ")}
                >
                  <span className="flex items-start justify-between gap-3">
                    <span>
                      <span className="block text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {profile.label}
                      </span>
                      <span className="mt-1 block text-xs leading-5 text-slate-500 dark:text-slate-400">
                        {profile.example_company}
                      </span>
                    </span>
                    <span
                      className={[
                        "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px]",
                        selected
                          ? "border-blue-600 bg-blue-600 text-white"
                          : "border-slate-300 text-transparent dark:border-gray-600",
                      ].join(" ")}
                    >
                      ✓
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {selectedDemoProfile && (
          <section className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-gray-700 dark:bg-gray-800/60">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                  {selectedDemoProfile.example_company}
                </p>
                <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-400">
                  {selectedDemoProfile.description}
                </p>
              </div>
              <span className="shrink-0 rounded-full bg-white px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-blue-700 shadow-sm dark:bg-gray-900 dark:text-blue-300">
                Selected
              </span>
            </div>
            <ul className="mt-3 grid gap-2 text-xs text-slate-700 dark:text-slate-300 sm:grid-cols-2">
              {selectedDemoProfile.highlights.map((highlight) => (
                <li key={highlight} className="flex gap-2">
                  <span className="text-emerald-600">✓</span>
                  <span>{highlight}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-800 dark:bg-red-950 dark:text-red-400">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <button
            type="submit"
            disabled={provision.isPending || !selectedDemoProfile || demoProfilesQuery.isError}
            className="w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-50"
          >
            {provision.isPending
              ? "Building your sample company…"
              : `Open ${selectedDemoProfile?.label ?? "sample"} demo →`}
          </button>
          <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-slate-500">
            <span>Usually ready in under a minute</span>
            <button
              type="button"
              onClick={() => setDemoMode(false)}
              className="font-semibold text-blue-700 hover:text-blue-800 dark:text-blue-400"
            >
              Set up a live company instead
            </button>
          </div>
        </div>
      </form>
    );
  }

  return (
    <form
      onSubmit={submitProvision}
      className="space-y-5"
    >
      <div>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-1">
          Your company
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          A few quick questions so your CFO advisor knows your business from day one.
        </p>
      </div>

      <fieldset className="rounded-xl border border-blue-200 bg-blue-50/60 p-4 dark:border-blue-900 dark:bg-blue-950/20">
        <label className="flex cursor-pointer items-start gap-3">
          <input
            type="checkbox"
            checked={demoMode}
            onChange={(e) => {
              const enabled = e.target.checked;
              setDemoMode(enabled);
              if (enabled) {
                const profile = demoProfiles.find((item) => item.key === demoProfile);
                if (profile && !legalName.trim()) {
                  setLegalName(profile.example_company);
                  setDisplayName(profile.example_company);
                }
              } else if (selectedDemoProfile && legalName === selectedDemoProfile.example_company) {
                setLegalName("");
                setDisplayName("");
              }
            }}
            className="mt-1 h-4 w-4 rounded border-blue-300 text-blue-600"
          />
          <span>
            <span className="block text-sm font-semibold text-slate-900 dark:text-slate-100">
              Start with a sample company
            </span>
            <span className="mt-0.5 block text-xs leading-5 text-slate-600 dark:text-slate-400">
              Explore a complete workflow with disposable dummy data. The company is clearly marked Demo and never connects to live banks or operating systems.
            </span>
          </span>
        </label>

        {demoMode && (
          <div className="mt-4 border-t border-blue-200 pt-4 dark:border-blue-900">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-wide text-blue-800 dark:text-blue-300">
                Industry sample
              </span>
              <select
                value={demoProfile}
                onChange={(e) => selectDemoProfile(e.target.value)}
                disabled={demoProfilesQuery.isLoading}
                className="mt-1 w-full rounded-lg border border-blue-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-blue-900 dark:bg-gray-900 dark:text-slate-100"
              >
                {demoProfiles.map((profile) => (
                  <option key={profile.key} value={profile.key}>{profile.label}</option>
                ))}
              </select>
            </label>
            {demoProfilesQuery.isLoading && (
              <p className="mt-2 text-xs text-slate-500">Loading industry samples…</p>
            )}
            {demoProfilesQuery.isError && (
              <p className="mt-2 text-xs text-red-600">Industry samples could not be loaded. Refresh and try again.</p>
            )}
            {selectedDemoProfile && (
              <div className="mt-3 rounded-lg border border-blue-100 bg-white p-3 dark:border-blue-900/70 dark:bg-gray-900">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {selectedDemoProfile.example_company}
                    </p>
                    <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-400">
                      {selectedDemoProfile.description}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full bg-blue-100 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-blue-700 dark:bg-blue-900 dark:text-blue-200">
                    Demo
                  </span>
                </div>
                <ul className="mt-3 grid gap-1.5 text-xs text-slate-700 dark:text-slate-300 sm:grid-cols-2">
                  {selectedDemoProfile.highlights.map((highlight) => (
                    <li key={highlight} className="flex gap-1.5">
                      <span className="text-emerald-600">✓</span>
                      <span>{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </fieldset>

      <label className="block">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
          Legal name <span className="text-red-500">*</span>
        </span>
        <input
          type="text"
          required
          autoFocus
          placeholder="Acme Roofing LLC"
          value={legalName}
          onChange={(e) => setLegalName(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
        />
      </label>

      <fieldset className="rounded-xl border border-slate-200 p-4 dark:border-gray-700">
        <legend className="px-1 text-sm font-semibold text-slate-800 dark:text-slate-200">
          Who handles the books today?
        </legend>
        <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
          We start simple and only suggest approvals when real people and use support them.
        </p>
        <select
          value={accountingModel}
          onChange={(e) => {
            const value = e.target.value;
            setAccountingModel(value);
            if (value === "owner_only" || value === "not_sure") {
              setAccountingPeople(1);
              setIndependentReviewer(false);
              setPrioritySources([]);
              setMaterialityThreshold("");
            } else if (accountingPeople < 2) {
              setAccountingPeople(2);
            }
          }}
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
        >
          <option value="owner_only">Just me</option>
          <option value="owner_bookkeeper">Owner and bookkeeper</option>
          <option value="external_accountant">External accountant or bookkeeper</option>
          <option value="finance_team">Accounting or finance team</option>
          <option value="not_sure">Not sure yet</option>
        </select>

        {accountingModel !== "owner_only" && accountingModel !== "not_sure" && (
          <div className="mt-3 space-y-3">
            <label className="block">
              <span className="text-xs font-medium text-slate-700 dark:text-slate-300">People involved in accounting</span>
              <input
                type="number"
                min={2}
                max={100}
                value={accountingPeople}
                onChange={(e) => setAccountingPeople(Math.max(2, Number(e.target.value)))}
                className="mt-1 w-28 rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
              />
            </label>
            <label className="flex items-start gap-2 text-xs text-slate-700 dark:text-slate-300">
              <input
                type="checkbox"
                checked={independentReviewer}
                onChange={(e) => setIndependentReviewer(e.target.checked)}
                className="mt-0.5"
              />
              A second person will have their own Ledger Core login and can review material work.
            </label>

            {independentReviewer && (
              <div className="rounded-lg bg-slate-50 p-3 dark:bg-gray-800/70">
                <p className="text-xs font-medium text-slate-700 dark:text-slate-300">Where might review help first? <span className="font-normal text-slate-400">Optional</span></p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {[
                    ["manual_journal", "Manual journals"],
                    ["ap_invoice", "Vendor bills"],
                    ["ar_invoice", "Customer invoices"],
                    ["purchase_order", "Purchase orders"],
                    ["sub_pay_app", "Subcontractor pay applications"],
                  ].map(([value, label]) => (
                    <label key={value} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs dark:border-gray-700 dark:bg-gray-900">
                      <input
                        type="checkbox"
                        checked={prioritySources.includes(value)}
                        onChange={(e) => setPrioritySources((current) => e.target.checked ? [...current, value] : current.filter((item) => item !== value))}
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <label className="mt-3 block">
                  <span className="text-xs text-slate-600 dark:text-slate-400">Material review amount <span className="text-slate-400">(optional)</span></span>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="text-sm text-slate-500">$</span>
                    <input
                      type="number"
                      min={1}
                      step="0.01"
                      placeholder="5000"
                      value={materialityThreshold}
                      onChange={(e) => setMaterialityThreshold(e.target.value)}
                      className="w-36 rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-900 dark:text-slate-100"
                    />
                  </div>
                  <span className="mt-1 block text-[11px] text-slate-400">This records your preference. It does not turn an approval on.</span>
                </label>
              </div>
            )}
          </div>
        )}
        {(accountingModel === "owner_only" || accountingModel === "not_sure") && (
          <p className="mt-3 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">
            Simple mode: routine approvals stay off. Reconciliation, evidence, reversal, posting-period, and close safeguards remain on.
          </p>
        )}
      </fieldset>

      <label className="block">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
          Display name{" "}
          <span className="text-slate-400 font-normal">(shown in the app)</span>
        </span>
        <input
          type="text"
          placeholder="Acme Roofing"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
        />
      </label>

      <label className="block">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
          What are you building?{" "}
          <span className="text-slate-400 font-normal">(optional — your CFO will know from day one)</span>
        </span>
        <input
          type="text"
          placeholder="e.g. B2B dev-tools SaaS, commercial roofing sub, e-commerce brand…"
          value={whatBuilding}
          onChange={(e) => setWhatBuilding(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
        />
      </label>

      <label className="block">
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
          Who are your customers?{" "}
          <span className="text-slate-400 font-normal">(optional)</span>
        </span>
        <input
          type="text"
          placeholder="e.g. mid-market SaaS companies, general contractors, DTC consumers…"
          value={whoCustomers}
          onChange={(e) => setWhoCustomers(e.target.value)}
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
        />
      </label>

      <div className="grid grid-cols-2 gap-4">
        <label className="block">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            EIN{" "}
            <span className="text-slate-400 font-normal">(optional)</span>
          </span>
          <input
            type="text"
            placeholder="12-3456789"
            value={ein}
            onChange={(e) => setEin(e.target.value)}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
          />
        </label>

        <label className="block">
          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
            Fiscal year starts
          </span>
          <select
            value={fyMonth}
            onChange={(e) => setFyMonth(Number(e.target.value))}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
          >
            {[
              "January", "February", "March", "April", "May", "June",
              "July", "August", "September", "October", "November", "December",
            ].map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 dark:bg-red-950 dark:border-red-800 dark:text-red-400">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={provision.isPending || !legalName.trim() || (demoMode && (!selectedDemoProfile || demoProfilesQuery.isError))}
        className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
      >
        {provision.isPending
          ? (demoMode ? "Building your sample company…" : "Setting up…")
          : (demoMode ? "Create sample company →" : "Continue →")}
      </button>
    </form>
  );
}

// ── Step 2: Plan picker ───────────────────────────────────────────────────────

function PlanStep({
  entityId,
  onSkip,
}: {
  entityId: string;
  onSkip: () => void;
}) {
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const plansQuery = useQuery(saasPlansQueryOptions());
  const plans = (plansQuery.data ?? []).filter((p: Plan) => p.status === "active");

  const subscribe = useMutation({
    mutationFn: (planId: string) =>
      api<SubscribeResponse>("/api/onboarding/subscribe", {
        method: "POST",
        body: { entity_id: entityId, plan_id: planId },
      }),
    onSuccess: ({ checkout_url }) => {
      window.location.href = checkout_url;
    },
    onError: (err) => setError(errorMessage(err)),
  });

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-1">
          Choose a plan
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          You can change this anytime. All plans include a 14-day free trial.
        </p>
      </div>

      {plansQuery.isLoading && (
        <div className="text-sm text-slate-400 py-4 text-center">Loading plans…</div>
      )}

      {plans.length > 0 && (
        <div className="space-y-3">
          {plans.map((plan: Plan) => (
            <button
              key={plan.id}
              type="button"
              onClick={() => setSelectedPlanId(plan.id)}
              className={[
                "w-full text-left rounded-xl border-2 p-4 transition-colors",
                selectedPlanId === plan.id
                  ? "border-blue-600 bg-blue-50 dark:bg-blue-950 dark:border-blue-500"
                  : "border-slate-200 hover:border-blue-300 dark:border-gray-700 dark:hover:border-blue-600",
              ].join(" ")}
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-slate-900 dark:text-slate-100">
                  {plan.name}
                </span>
                <span className="text-lg font-bold text-blue-700 dark:text-blue-400">
                  ${Number(plan.amount).toFixed(0)}
                  <span className="text-sm font-normal text-slate-500">
                    /{plan.interval}
                  </span>
                </span>
              </div>
              {plan.description && (
                <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                  {plan.description}
                </p>
              )}
            </button>
          ))}
        </div>
      )}

      {plans.length === 0 && !plansQuery.isLoading && (
        <div className="text-sm text-slate-500 bg-slate-50 rounded-lg p-4 dark:bg-gray-800">
          No plans available yet. You can set up billing from Settings later.
        </div>
      )}

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 dark:bg-red-950 dark:border-red-800 dark:text-red-400">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          disabled={!selectedPlanId || subscribe.isPending}
          onClick={() => selectedPlanId && subscribe.mutate(selectedPlanId)}
          className="flex-1 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {subscribe.isPending ? "Redirecting to payment…" : "Continue to payment →"}
        </button>
        <button
          type="button"
          onClick={onSkip}
          className="px-4 py-2.5 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}

// ── Step 4: Import books ──────────────────────────────────────────────────────

function ImportStep({ onDone }: { onDone: () => void }) {
  const navigate = useNavigate();
  const [sheetsUrl, setSheetsUrl] = useState("");
  const [sheetsState, setSheetsState] = useState<
    | { status: "idle" }
    | { status: "loading" }
    | { status: "preview"; detectedKind: string; rowCount: number; confidence: number }
    | { status: "success"; kind: string; message: string }
    | { status: "error"; message: string }
  >({ status: "idle" });

  async function fetchSheetsPreview() {
    if (!sheetsUrl.trim()) return;
    setSheetsState({ status: "loading" });
    try {
      const res = await api<{
        detected_kind: string | null;
        confidence: number;
        parsed_row_count: number;
        committed: boolean;
        skipped?: string[];
      }>("/api/imports/from-url", {
        method: "POST",
        body: { url: sheetsUrl.trim(), commit: false },
      });
      if (!res.detected_kind) {
        setSheetsState({ status: "error", message: res.skipped?.[0] ?? "Could not detect data type. Try specifying the type manually." });
        return;
      }
      setSheetsState({
        status: "preview",
        detectedKind: res.detected_kind,
        rowCount: res.parsed_row_count,
        confidence: res.confidence,
      });
    } catch (err) {
      setSheetsState({ status: "error", message: err instanceof Error ? err.message : "Failed to fetch sheet" });
    }
  }

  async function commitSheetsImport(kind: string) {
    setSheetsState({ status: "loading" });
    try {
      const res = await api<{ result: { created_count?: number } }>("/api/imports/from-url", {
        method: "POST",
        body: { url: sheetsUrl.trim(), kind, commit: true },
      });
      const count = res.result?.created_count ?? "?";
      setSheetsState({ status: "success", kind, message: `✓ Imported ${count} ${kind.replace(/_/g, " ")} from your sheet.` });
    } catch (err) {
      setSheetsState({ status: "error", message: err instanceof Error ? err.message : "Import failed" });
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-1">
          Import your books
        </h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Bring in your existing data so you can hit the ground running. You can
          also do this later from Settings → Imports.
        </p>
      </div>

      {/* Google Sheets import — the fast path */}
      <div className="rounded-xl border-2 border-blue-200 bg-blue-50 p-4 space-y-3 dark:border-blue-800 dark:bg-blue-950">
        <div className="flex items-center gap-2">
          <span className="text-xl">📊</span>
          <div>
            <div className="font-semibold text-slate-900 dark:text-slate-100 text-sm">
              Import from Google Sheets
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">
              Paste a link to any sheet shared as "Anyone with the link can view"
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <input
            type="url"
            placeholder="https://docs.google.com/spreadsheets/d/…"
            value={sheetsUrl}
            onChange={(e) => { setSheetsUrl(e.target.value); setSheetsState({ status: "idle" }); }}
            className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 dark:border-gray-700 dark:bg-gray-800 dark:text-slate-100"
            onKeyDown={(e) => e.key === "Enter" && void fetchSheetsPreview()}
          />
          <button
            type="button"
            onClick={() => void fetchSheetsPreview()}
            disabled={!sheetsUrl.trim() || sheetsState.status === "loading"}
            className="rounded-lg bg-blue-600 text-white text-sm px-4 hover:bg-blue-700 disabled:opacity-50"
          >
            {sheetsState.status === "loading" ? "…" : "Detect"}
          </button>
        </div>

        {sheetsState.status === "preview" && (
          <div className="rounded-lg bg-white border border-blue-200 p-3 space-y-2 dark:bg-gray-900 dark:border-blue-800">
            <div className="text-sm text-slate-700 dark:text-slate-300">
              Found <strong>{sheetsState.rowCount} rows</strong> of{" "}
              <strong>{sheetsState.detectedKind.replace(/_/g, " ")}</strong>
              {sheetsState.confidence < 1 && (
                <span className="ml-1 text-slate-400 text-xs">
                  ({Math.round(sheetsState.confidence * 100)}% confidence)
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => void commitSheetsImport(sheetsState.detectedKind)}
              className="w-full rounded-lg bg-blue-600 text-white text-sm py-2 hover:bg-blue-700"
            >
              Import {sheetsState.rowCount} rows →
            </button>
          </div>
        )}

        {sheetsState.status === "success" && (
          <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-700 dark:bg-green-950 dark:border-green-800 dark:text-green-400">
            {sheetsState.message}
          </div>
        )}

        {sheetsState.status === "error" && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600 dark:bg-red-950 dark:border-red-800 dark:text-red-400">
            {sheetsState.message}
          </div>
        )}
      </div>

      {/* Other import paths */}
      <div className="space-y-3">
        <button
          type="button"
          onClick={() => void navigate({ to: "/settings/imports-quickbooks" })}
          className="w-full text-left rounded-xl border-2 border-slate-200 hover:border-blue-300 p-4 transition-colors dark:border-gray-700 dark:hover:border-blue-600"
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">📦</span>
            <div>
              <div className="font-semibold text-slate-900 dark:text-slate-100">
                Import from QuickBooks
              </div>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Connect QuickBooks Online or upload a QBO / IIF file
              </div>
            </div>
          </div>
        </button>

        <button
          type="button"
          onClick={() => void navigate({ to: "/imports" })}
          className="w-full text-left rounded-xl border-2 border-slate-200 hover:border-blue-300 p-4 transition-colors dark:border-gray-700 dark:hover:border-blue-600"
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">📄</span>
            <div>
              <div className="font-semibold text-slate-900 dark:text-slate-100">
                Upload a CSV file
              </div>
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Chart of accounts, vendors, customers, transactions, and more
              </div>
            </div>
          </div>
        </button>
      </div>

      <button
        type="button"
        onClick={onDone}
        className="w-full rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition-colors"
      >
        {sheetsState.status === "success" ? "Continue to dashboard →" : "Skip for now →"}
      </button>
    </div>
  );
}
